// pages/mobile/mobileApi.js
// Calls to /api/mobile/ (backend/mobile). Every call is scoped server-side to
// the signed-in field staff member's own messages.
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../authentication/axiosInstance';

export const DETAIL_PATHS = {
  dispatch: 'dispatches',
  warning: 'warnings',
  stand_down: 'warnings',
  request: 'requests',
};

export const fetchMessages = () => api.get('/mobile/messages/').then((response) => response.data);

export const fetchDetail = (kind, id) =>
  api.get(`/mobile/${DETAIL_PATHS[kind]}/${id}/`).then((response) => response.data);

export const sendAnswer = (kind, id, answer, note = '') =>
  api.post('/mobile/answers/', { kind, id: Number(id), answer, note }).then((response) => response.data);

export const errorMessage = (error) => {
  if (error?.response?.data?.error === 'not_linked') return error.response.data.message;
  if (error?.response?.status === 404) return "We couldn't find that message.";
  return error?.response?.data?.error || 'Something went wrong. Check your connection and try again.';
};

export const REFRESH_INTERVAL_MS = 30000;

// A Home Screen launch can resume an existing window. Refresh its data on
// resume, reconnection, push arrival and while visible, without reloading forms.
export const useMobileData = (load) => {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const mounted = useRef(false);
  const sequence = useRef(0);
  const inFlight = useRef(false);

  const reload = useCallback(() => {
    const request = ++sequence.current;
    inFlight.current = true;
    setState((previous) => ({ ...previous, loading: true }));
    return Promise.resolve().then(load)
      .then((data) => {
        if (mounted.current && request === sequence.current) setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (mounted.current && request === sequence.current) setState({ data: null, error, loading: false });
      })
      .finally(() => {
        if (request === sequence.current) inFlight.current = false;
      });
  }, [load]);

  // A successful write is newer than reads that were already in flight.
  const replaceData = useCallback((data) => {
    sequence.current += 1;
    inFlight.current = false;
    if (mounted.current) setState({ data, error: null, loading: false });
  }, []);

  useEffect(() => {
    mounted.current = true;
    setState({ data: null, error: null, loading: true });
    reload();
    const refreshVisible = () => {
      if (document.visibilityState !== 'hidden' && navigator.onLine !== false && !inFlight.current) reload();
    };
    const onPush = (event) => {
      if (event.data?.type === 'mobile-message-updated') refreshVisible();
    };
    window.addEventListener('focus', refreshVisible);
    window.addEventListener('pageshow', refreshVisible);
    window.addEventListener('online', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    const worker = navigator.serviceWorker;
    worker?.addEventListener?.('message', onPush);
    const timer = setInterval(refreshVisible, REFRESH_INTERVAL_MS);
    return () => {
      mounted.current = false;
      sequence.current += 1;
      inFlight.current = false;
      clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      window.removeEventListener('pageshow', refreshVisible);
      window.removeEventListener('online', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
      worker?.removeEventListener?.('message', onPush);
    };
  }, [reload]);

  return { ...state, reload, replaceData };
};
